/**
 * Taxonomy Configuration
 * 
 * Central configuration for product hierarchy: Collection → ProductType → Category → Tag
 * Used by MegaMenu, ProductFilters, Breadcrumbs, and URL routing.
 * 
 * @see src/pages/admin/taxonomy-navigation-runbook.md for full documentation
 */

export interface TaxonomyNode {
  id: string;
  label: string;
  /** Optional truncated label for display in filters (removes repetitive parent terms) */
  displayLabel?: string;
  handle: string;
  type: 'collection' | 'productType' | 'category' | 'tag' | 'vendor';
  /** Which Shopify field to filter by */
  shopifyField: 'collection' | 'productType' | 'tag';
  /** Collection handle if this node belongs to a specific collection */
  collectionHandle?: string;
  /** Multiple tags for vendor groupings (used when type='vendor') */
  tags?: string[];
  children?: TaxonomyNode[];
}

// =============================================================================
// APPAREL COLLECTION
// =============================================================================

const BLOUSES_TAGS: TaxonomyNode[] = [
  { id: 'button-up-blouses', label: 'Button-Up Blouses', displayLabel: 'Button-Up', handle: 'button-up-blouses', type: 'tag', shopifyField: 'tag' },
  { id: 'button-detail-blouses', label: 'Button Detail Blouses', displayLabel: 'Button Detail', handle: 'button-detail-blouses', type: 'tag', shopifyField: 'tag' },
  { id: 'long-sleeve-blouses', label: 'Long Sleeve Blouses', displayLabel: 'Long Sleeve', handle: 'long-sleeve-blouses', type: 'tag', shopifyField: 'tag' },
  { id: 'mesh-blouses', label: 'Mesh Blouses', displayLabel: 'Mesh', handle: 'mesh-blouses', type: 'tag', shopifyField: 'tag' },
  { id: 'puff-sleeve-blouses', label: 'Puff Sleeve Blouses', displayLabel: 'Puff Sleeve', handle: 'puff-sleeve-blouses', type: 'tag', shopifyField: 'tag' },
  { id: 'short-sleeve-blouses', label: 'Short Sleeve Blouses', displayLabel: 'Short Sleeve', handle: 'short-sleeve-blouses', type: 'tag', shopifyField: 'tag' },
  { id: 'tie-neck-blouses', label: 'Tie Neck Blouses', displayLabel: 'Tie Neck', handle: 'tie-neck-blouses', type: 'tag', shopifyField: 'tag' },
];

const SHIRTS_TAGS: TaxonomyNode[] = [
  { id: 'button-up-shirts', label: 'Button-Up Shirts', displayLabel: 'Button-Up', handle: 'button-up-shirts', type: 'tag', shopifyField: 'tag' },
  { id: 'button-detail-shirts', label: 'Button Detail Shirts', displayLabel: 'Button Detail', handle: 'button-detail-shirts', type: 'tag', shopifyField: 'tag' },
  { id: 'long-sleeve-shirts', label: 'Long Sleeve Shirts', displayLabel: 'Long Sleeve', handle: 'long-sleeve-shirts', type: 'tag', shopifyField: 'tag' },
  { id: 'short-sleeve-shirts', label: 'Short Sleeve Shirts', displayLabel: 'Short Sleeve', handle: 'short-sleeve-shirts', type: 'tag', shopifyField: 'tag' },
];

const TSHIRTS_TAGS: TaxonomyNode[] = [
  { id: 'long-sleeve-t-shirts', label: 'Long Sleeve T-Shirts', displayLabel: 'Long Sleeve', handle: 'long-sleeve-t-shirts', type: 'tag', shopifyField: 'tag' },
  { id: 'short-sleeve-t-shirts', label: 'Short Sleeve T-Shirts', displayLabel: 'Short Sleeve', handle: 'short-sleeve-t-shirts', type: 'tag', shopifyField: 'tag' },
];

const TOPS_CATEGORIES: TaxonomyNode[] = [
  { id: 'blouses', label: 'Blouses', handle: 'blouses', type: 'category', shopifyField: 'tag', children: BLOUSES_TAGS },
  { id: 'bodysuits', label: 'Bodysuits', handle: 'bodysuits', type: 'category', shopifyField: 'tag' },
  { id: 'camis', label: 'Camis', handle: 'camis', type: 'category', shopifyField: 'tag' },
  { id: 'cardigans', label: 'Cardigans', handle: 'cardigans', type: 'category', shopifyField: 'tag' },
  { id: 'hoodies', label: 'Hoodies', handle: 'hoodies', type: 'category', shopifyField: 'tag' },
  { id: 'jackets', label: 'Jackets', handle: 'jackets', type: 'category', shopifyField: 'tag' },
  { id: 'kimonos', label: 'Kimonos', handle: 'kimonos', type: 'category', shopifyField: 'tag' },
  { id: 'shirts', label: 'Shirts', handle: 'shirts', type: 'category', shopifyField: 'tag', children: SHIRTS_TAGS },
  { id: 'sweaters', label: 'Sweaters', handle: 'sweaters', type: 'category', shopifyField: 'tag' },
  { id: 'sweatshirts', label: 'Sweatshirts', handle: 'sweatshirts', type: 'category', shopifyField: 'tag' },
  { id: 'tanks', label: 'Tanks', handle: 'tanks', type: 'category', shopifyField: 'tag' },
  { id: 't-shirts', label: 'T-Shirts', handle: 't-shirts', type: 'category', shopifyField: 'tag', children: TSHIRTS_TAGS },
  { id: 'vests', label: 'Vests', handle: 'vests', type: 'category', shopifyField: 'tag' },
];

const JEANS_TAGS: TaxonomyNode[] = [
  { id: 'cropped-jeans', label: 'Cropped Jeans', displayLabel: 'Cropped', handle: 'cropped-jeans', type: 'tag', shopifyField: 'tag' },
  { id: 'flare-jeans', label: 'Flare Jeans', displayLabel: 'Flare', handle: 'flare-jeans', type: 'tag', shopifyField: 'tag' },
  { id: 'wide-leg-jeans', label: 'Wide Leg Jeans', displayLabel: 'Wide Leg', handle: 'wide-leg-jeans', type: 'tag', shopifyField: 'tag' },
];

// Wide Leg sub-tags (nested under Wide Leg Pants)
const WIDE_LEG_PANTS_TAGS: TaxonomyNode[] = [
  { id: 'wide-leg-cords-ribs', label: 'Wide Leg Cords & Ribs', displayLabel: 'Cords & Ribs', handle: 'wide-leg-cords-ribs', type: 'tag', shopifyField: 'tag' },
  { id: 'wide-leg-bright-solids', label: 'Wide Leg Bright Solids', displayLabel: 'Bright Solids', handle: 'wide-leg-bright-solids', type: 'tag', shopifyField: 'tag' },
  { id: 'wide-leg-neutral-solids', label: 'Wide Leg Neutral Solids', displayLabel: 'Neutral Solids', handle: 'wide-leg-neutral-solids', type: 'tag', shopifyField: 'tag' },
  { id: 'wide-leg-limited', label: 'Wide Leg Limited', displayLabel: 'Limited', handle: 'wide-leg-limited', type: 'tag', shopifyField: 'tag' },
  { id: 'wide-leg-prints', label: 'Wide Leg Prints', displayLabel: 'Prints', handle: 'wide-leg-prints', type: 'tag', shopifyField: 'tag' },
  { id: 'wide-leg-stripes', label: 'Wide Leg Stripes', displayLabel: 'Stripes', handle: 'wide-leg-stripes', type: 'tag', shopifyField: 'tag' },
  { id: 'wide-leg-cropped', label: 'Wide Leg Cropped', displayLabel: 'Cropped', handle: 'wide-leg-cropped', type: 'tag', shopifyField: 'tag' },
];

const PANTS_TAGS: TaxonomyNode[] = [
  { id: 'cargo-pants', label: 'Cargo Pants', displayLabel: 'Cargo', handle: 'cargo-pants', type: 'tag', shopifyField: 'tag' },
  { id: 'flare-pants', label: 'Flare Pants', displayLabel: 'Flare', handle: 'flare-pants', type: 'tag', shopifyField: 'tag' },
  { id: 'wide-leg-pants', label: 'Wide Leg Pants', displayLabel: 'Wide Leg', handle: 'wide-leg-pants', type: 'tag', shopifyField: 'tag', children: WIDE_LEG_PANTS_TAGS },
];

const SHORTS_TAGS: TaxonomyNode[] = [
  { id: 'denim-shorts', label: 'Denim Shorts', handle: 'denim-shorts', type: 'tag', shopifyField: 'tag' },
  { id: 'elastic-waist-shorts', label: 'Elastic Waist Shorts', handle: 'elastic-waist-shorts', type: 'tag', shopifyField: 'tag' },
  { id: 'tie-waist-shorts', label: 'Tie Waist Shorts', handle: 'tie-waist-shorts', type: 'tag', shopifyField: 'tag' },
];

const SKIRTS_TAGS: TaxonomyNode[] = [
  { id: 'maxi-skirts', label: 'Maxi Skirts', handle: 'maxi-skirts', type: 'tag', shopifyField: 'tag' },
  { id: 'midi-skirts', label: 'Midi Skirts', handle: 'midi-skirts', type: 'tag', shopifyField: 'tag' },
  { id: 'mini-skirts', label: 'Mini Skirts', handle: 'mini-skirts', type: 'tag', shopifyField: 'tag' },
];

const BOTTOMS_CATEGORIES: TaxonomyNode[] = [
  { id: 'jeans', label: 'Jeans', handle: 'jeans', type: 'category', shopifyField: 'tag', children: JEANS_TAGS },
  { id: 'joggers', label: 'Joggers', handle: 'joggers', type: 'category', shopifyField: 'tag' },
  { id: 'pants', label: 'Pants', handle: 'pants', type: 'category', shopifyField: 'tag', children: PANTS_TAGS },
  { id: 'shorts', label: 'Shorts', handle: 'shorts', type: 'category', shopifyField: 'tag', children: SHORTS_TAGS },
  { id: 'skirts', label: 'Skirts', handle: 'skirts', type: 'category', shopifyField: 'tag', children: SKIRTS_TAGS },
];

const OUTFIT_SETS_TAGS: TaxonomyNode[] = [
  { id: 'top-pants-set', label: 'Top & Pants Set', handle: 'top-pants-set', type: 'tag', shopifyField: 'tag' },
  { id: 'top-shorts-set', label: 'Top & Shorts Set', handle: 'top-shorts-set', type: 'tag', shopifyField: 'tag' },
  { id: 'top-skirt-set', label: 'Top & Skirt Set', handle: 'top-skirt-set', type: 'tag', shopifyField: 'tag' },
];

// Outfit Sets don't need a redundant category wrapper - tags are direct children
const OUTFIT_SETS_CATEGORIES: TaxonomyNode[] = OUTFIT_SETS_TAGS;

export const APPAREL_TAXONOMY: TaxonomyNode = {
  id: 'apparel',
  label: 'Apparel',
  handle: 'apparel',
  type: 'collection',
  shopifyField: 'collection',
  collectionHandle: 'apparel',
  children: [
    { id: 'tops', label: 'Tops', handle: 'tops', type: 'productType', shopifyField: 'productType', collectionHandle: 'apparel', children: TOPS_CATEGORIES },
    { id: 'bottoms', label: 'Bottoms', handle: 'bottoms', type: 'productType', shopifyField: 'productType', collectionHandle: 'apparel', children: BOTTOMS_CATEGORIES },
    { id: 'outfit-sets', label: 'Outfit Sets', handle: 'outfit-sets', type: 'productType', shopifyField: 'productType', collectionHandle: 'apparel', children: OUTFIT_SETS_CATEGORIES },
  ],
};

// =============================================================================
// LOUNGEWEAR COLLECTION
// =============================================================================

const LOUNGEWEAR_TAGS: TaxonomyNode[] = [
  { id: 'lounge-set', label: 'Lounge Set', handle: 'lounge-set', type: 'tag', shopifyField: 'tag' },
  { id: 'pajamas', label: 'Pajamas', handle: 'pajamas', type: 'tag', shopifyField: 'tag' },
  { id: 'pajamas-set', label: 'Pajamas Set', handle: 'pajamas-set', type: 'tag', shopifyField: 'tag' },
];

const LOUNGEWEAR_CATEGORIES: TaxonomyNode[] = [
  { id: 'loungewear-cat', label: 'Loungewear', handle: 'loungewear', type: 'category', shopifyField: 'tag', children: LOUNGEWEAR_TAGS },
];

export const LOUNGEWEAR_TAXONOMY: TaxonomyNode = {
  id: 'loungewear',
  label: 'Loungewear',
  handle: 'loungewear',
  type: 'collection',
  shopifyField: 'collection',
  collectionHandle: 'lounge-wear',
  children: [
    { id: 'loungewear-type', label: 'Loungewear', handle: 'loungewear', type: 'productType', shopifyField: 'productType', collectionHandle: 'lounge-wear', children: LOUNGEWEAR_CATEGORIES },
  ],
};

// =============================================================================
// DRESSES COLLECTION
// =============================================================================

const DRESSES_TAGS: TaxonomyNode[] = [
  { id: 'maxi-dresses', label: 'Maxi Dresses', handle: 'maxi-dresses', type: 'tag', shopifyField: 'tag' },
  { id: 'midi-dresses', label: 'Midi Dresses', handle: 'midi-dresses', type: 'tag', shopifyField: 'tag' },
  { id: 'mini-dresses', label: 'Mini Dresses', handle: 'mini-dresses', type: 'tag', shopifyField: 'tag' },
];

// Dresses don't need a redundant category wrapper - tags are direct children
const DRESSES_CATEGORIES: TaxonomyNode[] = DRESSES_TAGS;

export const DRESSES_TAXONOMY: TaxonomyNode = {
  id: 'dresses',
  label: 'Dresses',
  handle: 'dresses',
  type: 'collection',
  shopifyField: 'collection',
  collectionHandle: 'dresses',
  children: [
    { id: 'dresses-type', label: 'Dresses', handle: 'dresses', type: 'productType', shopifyField: 'productType', collectionHandle: 'dresses', children: DRESSES_CATEGORIES },
  ],
};

// =============================================================================
// ONE-PIECES COLLECTION
// =============================================================================

const ONE_PIECES_TAGS: TaxonomyNode[] = [
  { id: 'jumpsuits', label: 'Jumpsuits', handle: 'jumpsuits', type: 'tag', shopifyField: 'tag' },
  { id: 'overalls', label: 'Overalls', handle: 'overalls', type: 'tag', shopifyField: 'tag' },
  { id: 'rompers', label: 'Rompers', handle: 'rompers', type: 'tag', shopifyField: 'tag' },
];

// One-Pieces don't need a redundant category wrapper - tags are direct children
const ONE_PIECES_CATEGORIES: TaxonomyNode[] = ONE_PIECES_TAGS;

export const ONE_PIECES_TAXONOMY: TaxonomyNode = {
  id: 'one-pieces',
  label: 'One-Pieces',
  handle: 'one-pieces',
  type: 'collection',
  shopifyField: 'collection',
  collectionHandle: 'one-pieces',
  // Flat structure - tags are direct children (no redundant productType wrapper)
  children: ONE_PIECES_TAGS,
};

// =============================================================================
// SECONDARY CATEGORIES (2-level hierarchy)
// =============================================================================

// Hot Chocolate Design shoe tags
const HOT_CHOCOLATE_DESIGN_TAGS = [
  'Mary Jane Flats',
  'Mary Jane Pumps',
  'Mary Jane Platforms',
  'Casual Sneaker',
  'Casual Sandals',
  'Mary Jane Clogs',
];

// Colorstep Fashion leather shoe tags
const COLORSTEP_FASHION_TAGS = [
  'Leather Calf Boots',
  'Leather Ankle Boots',
  'Leather Booties',
  'Leather Wedge Sandals',
  'Leather Block Heels',
  'Leather Kitten Heels',
  'Leather Brogue Oxfords',
  'Leather Slides',
  'Leather Platform Oxfords',
  'Leather Sandals',
  'Leather Loafers',
];

export const FOOTWEAR_TAXONOMY: TaxonomyNode = {
  id: 'footwear',
  label: 'Footwear',
  handle: 'footwear',
  type: 'collection',
  shopifyField: 'collection',
  collectionHandle: 'footwear',
  children: [
    { 
      id: 'hot-chocolate-design', 
      label: 'Hot Chocolate Design', 
      handle: 'hot-chocolate-design', 
      type: 'vendor', 
      shopifyField: 'tag',
      tags: HOT_CHOCOLATE_DESIGN_TAGS,
    },
    { 
      id: 'colorstep-fashion', 
      label: 'Colorstep Fashion co.Ltd', 
      handle: 'colorstep-fashion', 
      type: 'vendor', 
      shopifyField: 'tag',
      tags: COLORSTEP_FASHION_TAGS,
    },
  ],
};

export const ACCESSORIES_TAXONOMY: TaxonomyNode = {
  id: 'accessories',
  label: 'Accessories',
  handle: 'accessories',
  type: 'collection',
  shopifyField: 'collection',
  collectionHandle: 'accessories',
  children: [
    { id: 'bag-charms', label: 'Bag Charms', handle: 'bag-charms', type: 'tag', shopifyField: 'tag' },
    { id: 'belts', label: 'Belts', handle: 'belts', type: 'tag', shopifyField: 'tag' },
    { id: 'hats', label: 'Hats', handle: 'hats', type: 'tag', shopifyField: 'tag' },
    { id: 'hair-accessories', label: 'Hair Accessories', handle: 'hair-accessories', type: 'tag', shopifyField: 'tag' },
    { id: 'sunglasses', label: 'Sunglasses', handle: 'sunglasses', type: 'tag', shopifyField: 'tag' },
  ],
};

export const JEWELRY_TAXONOMY: TaxonomyNode = {
  id: 'jewelry',
  label: 'Jewelry',
  handle: 'jewelry',
  type: 'collection',
  shopifyField: 'collection',
  collectionHandle: 'jewelry',
  children: [
    { id: 'necklaces', label: 'Necklaces', handle: 'necklaces', type: 'tag', shopifyField: 'tag' },
    { id: 'earrings', label: 'Earrings', handle: 'earrings', type: 'tag', shopifyField: 'tag' },
    { id: 'bracelets', label: 'Bracelets', handle: 'bracelets', type: 'tag', shopifyField: 'tag' },
    { id: 'rings', label: 'Rings', handle: 'rings', type: 'tag', shopifyField: 'tag' },
  ],
};

export const BAGS_TAXONOMY: TaxonomyNode = {
  id: 'bags',
  label: 'Bags',
  handle: 'bags',
  type: 'collection',
  shopifyField: 'collection',
  collectionHandle: 'bags',
  children: [
    { id: 'crossbody-bags', label: 'Crossbody Bags', handle: 'crossbody-bags', type: 'tag', shopifyField: 'tag' },
    { id: 'handbags', label: 'Handbags', handle: 'handbags', type: 'tag', shopifyField: 'tag' },
    { id: 'shoulder-bags', label: 'Shoulder Bags', handle: 'shoulder-bags', type: 'tag', shopifyField: 'tag' },
    { id: 'tote-bags', label: 'Tote Bags', handle: 'tote-bags', type: 'tag', shopifyField: 'tag' },
    { id: 'weekender-bags', label: 'Weekender Bags', handle: 'weekender-bags', type: 'tag', shopifyField: 'tag' },
  ],
};

// Tag groupings for Lifestyle & Home (each menu item maps to multiple Shopify tags)
export const LIFESTYLE_TAG_GROUPS: Record<string, string[]> = {
  'table-decor': ['Vases', 'Table Decor', 'Tabletop Ornament', 'Table Lamps', 'Photo Frames'],
  'furniture': ['Console Tables', 'Wall Mirrors', 'Benches', 'Area Rugs', 'Ottoman'],
  'area-rugs': ['Area Rugs'],
  'bedding-sets': ['Bedding Sets'],
  'wall-mirrors': ['Wall Mirrors'],
  'throw-blankets': ['Throw Blankets'],
  // Wall Hangings (Macrame + Agate)
  'wall-hangings': ['Wall Hangings'],
  'macrame-door-curtains': ['Macrame Door Curtains'],
  'macrame-window-curtains': ['Macrame Window Curtains'],
  'macrame-vertical-banners': ['Macrame Vertical Banners'],
  'macrame-leaves': ['Macrame Leaves'],
  'agate-wall-hanging': ['Agate Wall Hanging'],
};

// Wall Hangings tags (Macrame and Agate)
const WALL_HANGINGS_TAGS = [
  'Macrame Door Curtains',
  'Macrame Window Curtains',
  'Macrame Vertical Banners',
  'Macrame Leaves',
  'Agate Wall Hanging',
];

export const LIFESTYLE_DECOR_TAXONOMY: TaxonomyNode = {
  id: 'lifestyle-decor',
  label: 'Lifestyle & Decor',
  handle: 'lifestyle-decor',
  type: 'collection',
  shopifyField: 'collection',
  collectionHandle: 'lifestyle-decor',
  children: [
    { id: 'area-rugs', label: 'Area Rugs', handle: 'area-rugs', type: 'tag', shopifyField: 'tag' },
    { id: 'accent-furniture', label: 'Accent Furniture', handle: 'furniture', type: 'tag', shopifyField: 'tag' },
    { id: 'bedding-sets', label: 'Bedding Sets', handle: 'bedding-sets', type: 'tag', shopifyField: 'tag' },
    { id: 'table-decor', label: 'Table Decor', handle: 'table-decor', type: 'tag', shopifyField: 'tag' },
    { id: 'wall-mirrors', label: 'Wall Mirrors', handle: 'wall-mirrors', type: 'tag', shopifyField: 'tag' },
    { id: 'throw-blankets', label: 'Throw Blankets', handle: 'throw-blankets', type: 'tag', shopifyField: 'tag' },
    { 
      id: 'wall-hangings', 
      label: 'Wall Hangings', 
      handle: 'wall-hangings', 
      type: 'vendor', 
      shopifyField: 'tag',
      tags: WALL_HANGINGS_TAGS,
    },
  ],
};

// =============================================================================
// ROOT TAXONOMY (All collections)
// =============================================================================

export const ROOT_TAXONOMY: TaxonomyNode[] = [
  APPAREL_TAXONOMY,
  LOUNGEWEAR_TAXONOMY,
  DRESSES_TAXONOMY,
  ONE_PIECES_TAXONOMY,
  FOOTWEAR_TAXONOMY,
  ACCESSORIES_TAXONOMY,
  JEWELRY_TAXONOMY,
  BAGS_TAXONOMY,
  LIFESTYLE_DECOR_TAXONOMY,
];

// =============================================================================
// COMBINED FILTER NODES (for unified display on pages)
// =============================================================================

/** Combined clothing filter nodes for /collections/apparel page - flat list of all main clothing types 
 * NOTE: No collectionHandle set - these filter by productType/tag only since the page already pre-filters products
 */
export const CLOTHING_FILTER_NODES: TaxonomyNode[] = [
  { id: 'tops', label: 'Tops', handle: 'tops', type: 'productType', shopifyField: 'productType', children: TOPS_CATEGORIES },
  { id: 'bottoms', label: 'Bottoms', handle: 'bottoms', type: 'productType', shopifyField: 'productType', children: BOTTOMS_CATEGORIES },
  { id: 'outfit-sets', label: 'Outfit Sets', handle: 'outfit-sets', type: 'productType', shopifyField: 'productType', children: OUTFIT_SETS_CATEGORIES },
  { id: 'dresses-filter', label: 'Dresses', handle: 'dresses', type: 'productType', shopifyField: 'productType', children: DRESSES_CATEGORIES },
  { id: 'one-pieces-filter', label: 'One-Pieces', handle: 'one-pieces', type: 'productType', shopifyField: 'productType', children: ONE_PIECES_CATEGORIES },
];

// =============================================================================
// LOOKUP MAPS (for fast access)
// =============================================================================

/** Map of collection handle to taxonomy node */
export const TAXONOMY_BY_COLLECTION: Record<string, TaxonomyNode> = {
  'apparel': APPAREL_TAXONOMY,
  'loungewear': LOUNGEWEAR_TAXONOMY,
  'dresses': DRESSES_TAXONOMY,
  'one-pieces': ONE_PIECES_TAXONOMY,
  'footwear': FOOTWEAR_TAXONOMY,
  'accessories': ACCESSORIES_TAXONOMY,
  'jewelry': JEWELRY_TAXONOMY,
  'bags': BAGS_TAXONOMY,
  'lifestyle-decor': LIFESTYLE_DECOR_TAXONOMY,
};

/** Collections that use the full 3-level hierarchy */
export const HIERARCHICAL_COLLECTIONS = ['apparel', 'loungewear', 'dresses', 'one-pieces'];

/** Collections that use simplified 2-level hierarchy */
export const FLAT_COLLECTIONS = ['footwear', 'accessories', 'jewelry', 'bags', 'lifestyle-decor'];
